export type WhatsNewSectionKind = "new" | "improved" | "fixed";

export type WhatsNewSection = {
  readonly kind: WhatsNewSectionKind;
  readonly title: string;
  readonly items: readonly string[];
};

export type WhatsNewRelease = {
  readonly id: string;
  readonly versionLabel: string;
  readonly publishedAt: string;
  readonly publishedLabel: string;
  readonly title: string;
  readonly intro: string;
  readonly sections: readonly WhatsNewSection[];
};

export const WHATS_NEW_STORAGE_KEY = "truucost.whatsNew.seenReleaseId";

export const WHATS_NEW_RELEASES = [
  {
    "id": "v0.1.0-beta.32",
    "versionLabel": "Beta 32",
    "title": "Schneller finden, Angebote und Belege prüfen",
    "intro": "Weniger Wartezeit beim Nachschlagen und Auswerten. Neue Angebote und Rechnungen prüfen Sie direkt am Original.",
    "sections": [
      {
        "kind": "new",
        "title": "Neu",
        "items": [
          "GC/Odörfer- und Pichler-Angebote können Sie direkt am PDF prüfen und vergleichen, mit getrennten Varianten und ohne dauerhafte Speicherung.",
          "Danfoss-Angebote können Sie einzeln am PDF prüfen; ein Preisvergleich zwischen diesen Angeboten ist vorerst nicht verfügbar.",
          "Hilti-Warenrechnungen übernehmen Sie nach Ihrer Prüfung am PDF, mit belegten Preisen, Rabatten sowie Bestell- und Lieferangaben. Nach der Freigabe können Sie die Prüfung schließen und den Fortschritt im Importverlauf verfolgen."
        ]
      },
      {
        "kind": "improved",
        "title": "Verbessert",
        "items": [
          "Artikel, Kostenstellen, Belege und Dateien laden mit weniger Wartezeit; die App reagiert schneller, vor allem beim Öffnen und wenn mehrere Bereiche gleichzeitig laden.",
          "Angebote werden auch mit vielen Positionen zügiger mit Ihren bisherigen Einkaufspreisen verglichen.",
          "Gruppierte Auswertungen und „Export für Excel“ werden zügiger erstellt; die vollständige Auswahl bleibt erhalten."
        ]
      },
      {
        "kind": "fixed",
        "title": "Behoben",
        "items": [
          "Die Rücknahme einer neuen Neuprüfung funktioniert wieder; spätere Prüfentscheidungen bleiben geschützt.",
          "Nach dem Speichern einer Preisprüfung bleibt die ER-Nummer im Beleg erhalten."
        ]
      }
    ],
    "publishedAt": "2026-10-19",
    "publishedLabel": "19. Oktober 2026"
  },
  {
    id: "v0.1.0-beta.31",
    versionLabel: "Beta 31",
    publishedAt: "2026-10-10",
    publishedLabel: "10. Oktober 2026",
    title: "Artikel finden, Warenkörbe füllen und Einkäufe auswerten",
    intro: "Artikel gezielt suchen, Mengen direkt ändern und Ihre Einkäufe in der Analyse überblicken.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Artikel finden Sie mit mehreren Suchwörtern und Filtern für Hersteller, Großhändler, Kostenstelle und Kaufzeitraum.",
          "Die neue „Analyse“ zeigt Mehrkosten und Einkaufsvolumen nach Zeitraum, Großhändler und Hersteller.",
          "Im Warenkorb wählen Sie den Bestpreis-Zeitraum: 6 oder 12 Monate, 2 oder 3 Jahre.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Mengen ändern Sie direkt auf der Artikelseite im gewählten eigenen Warenkorb; die Hilfe erklärt den Ablauf.",
          "„Startseite“ steht wieder links im Menü; „Bestellungen“ wird auf dem Handy auch beim Vorlesen eindeutig benannt.",
          "Artikel, Belege und Kostenstellen zeigen 50 Einträge pro Seite; Kostenstellen laden schneller.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Bei „Eigener Wert …“ bleibt der Eingabefokus erhalten; Rückwege nach Ladefehlern behalten Ihre Filter und Auswahl.",
          "Eine unterbrochene Übernahme Ihrer früheren Preisanfrage lässt sich ohne doppelte Warenkörbe oder Positionen wiederholen.",
          "Gutschriften mit Lieferadresse zählen in den Kostenstellen nur noch einmal.",
        ],
      },
    ],
  },
  {
    id: "v0.1.0-beta.30",
    versionLabel: "Beta 30",
    publishedAt: "2026-10-05",
    publishedLabel: "5. Oktober 2026",
    title: "Einfacher: neues Menü und klare Startseite",
    intro: "Weniger Seiten, weniger Zahlen – Sie finden schneller, was Sie täglich brauchen.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Die große Suche oben findet Artikel und Belege; Bestellungen und Warenkorb erreichen Sie jederzeit rechts oben.",
          "Die Startseite zeigt, was zu erledigen ist, Ihre Warenkörbe, Ihre letzten Uploads und zuletzt angesehene Artikel und Belege. Belege laden Sie direkt dort hoch.",
          "„Mehrkosten“ zeigt die zehn Artikel mit den höchsten Mehrkosten der letzten zwölf Monate; unter „Positionen“ finden Sie jede einzelne Rechnungsposition.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Bestellungen, Preisanfragen, Angebote und Auftragsbestätigungen liegen gemeinsam unter „Bestellungen“; „Belege hochladen“ bündelt Hochladen und Importe.",
          "Die Rechnungsprüfung hat einen eigenen Menüpunkt mit den Reitern „Zu prüfen“, „In Klärung“ und „Erledigt“; gespeicherte Angebote vergleichen Sie im „Preisspiegel“.",
          "Listen und Artikelseiten zeigen weniger Spalten und Zahlen. Hilfe, Neuerungen und Einstellungen finden Sie im Konto-Menü.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Der Warenkorb zeigt den Bestpreis der letzten zwölf Monate auch dann, wenn Sie einen Artikel nur bei einem Großhändler gekauft haben.",
          "„Alle passenden Belege ansehen“ in der Suche führt jetzt in die Belegliste.",
          "„Export für Excel“ unter „Mehrkosten“ lädt die Datei jetzt zuverlässig herunter.",
        ],
      },
    ],
  },
  {
    id: "v0.1.0-beta.29",
    versionLabel: "Beta 29",
    publishedAt: "2026-09-24",
    publishedLabel: "24. September 2026",
    title: "Einkauf und Belege vom Warenkorb bis zur Prüfung",
    intro: "Artikel sammeln, Bestellungen vorbereiten und Belege gemeinsam prüfen – mit dem Original als Grundlage.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Sie können Artikel in Warenkörben sammeln, Bestellungen je Großhändler vorbereiten und Sonderpositionen ergänzen; TruuCost versendet nichts automatisch.",
          "Rechnungen, Gutschriften, Angebote und Auftragsbestätigungen stehen gemeinsam unter „Belege“; offene Prüfungen können Sie selbst übernehmen.",
          "Unklare Kostenstellen bei Gutschriften können Berechtigte direkt am Original prüfen, begründet zuordnen oder offenlassen.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Artikel- und Kostenstellenansichten zeigen Preisverläufe übersichtlich; Sie können nach Kostenstelle, Großhändler und Beleg gruppieren und alle Treffer für Excel exportieren.",
          "Bei Belegen sehen Sie Prüfstand und Nachweise direkt in der Liste; Auftragsbestätigungen gehen zur Freigabe an Controlling.",
          "Die Artikelliste zeigt den aktuellen Warenkorb und lässt Artikel mit × wieder entfernen; Mengen und Kostenstellen lassen sich im Warenkorb leichter bearbeiten.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Neu eingelesene Gutschriften übernehmen eindeutig belegte Kostenstellen zuverlässiger; widersprüchliche Fälle bleiben zur Prüfung offen.",
          "Lange Artikelbeschreibungen und Filter passen besser auf den Bildschirm.",
        ],
      },
    ],
  },
  {
    "id": "v0.1.0-beta.28",
    "versionLabel": "Beta 28",
    "publishedAt": "2026-09-15",
    "publishedLabel": "15. September 2026",
    "title": "Angebote vergleichen und Belege gezielt prüfen",
    "intro": "Vom Preisvergleich bis zur geklärten Belegposition: Die nächsten Schritte sind direkt erreichbar und Ihre Auswahl bleibt erhalten.",
    "sections": [
      {
        "kind": "new",
        "title": "Neu",
        "items": [
          "Angebote vorläufig hochladen oder gespeicherte Angebote vergleichen, günstige Preise auswählen und die Zusammenstellung für später speichern.",
          "Rückfragen zu Angebotspreisen vorbereiten und exportieren – mit einem gemeinsamen Einkaufsziel für Angebote und neue Preisanfragen, ohne automatischen Versand.",
          "Menge, Netto-Einzelpreis und Positionssumme mit Vorschau berichtigen sowie unklare Artikel gezielt zuordnen – mit nachvollziehbarem Verlauf und erhaltenem Original."
        ]
      },
      {
        "kind": "improved",
        "title": "Verbessert",
        "items": [
          "Importhinweise sind nach Beleg gebündelt; passende Gründe, die nächste offene Position und die aktualisierte Hilfe führen durch die Prüfung.",
          "Der geführte Preisbericht zeigt neue offene Aufgaben seit dem letzten Bericht und führt von der Kostenstelle oder dem Artikel direkt zum Rechnungsnachweis.",
          "Persönliche Ansichten und Zeiträume bleiben auch auf anderen Geräten erhalten; Navigation, Rückwege und Hilfe passen zu den Arbeitsabläufen.",
          "Bei Anmeldeproblemen zeigt die App klarer, ob die Anmeldung oder der Zugriff auf den Betrieb nicht bestätigt werden konnte."
        ]
      },
      {
        "kind": "fixed",
        "title": "Behoben",
        "items": [
          "SHT-Preisangaben und Artikelgrenzen werden genauer eingelesen; unsichere Zuordnungen bleiben zur Prüfung sichtbar.",
          "Fehlende Originale werden bei Importen gesondert angezeigt, damit die nächste Prüfung zu einem verfügbaren PDF führt. Auch bei Angeboten erkennst du, wenn das Original-PDF nicht verfügbar ist; vorhandene Rechnungsnachweise bleiben erreichbar.",
          "Beim Wechsel von Benutzer oder Betrieb werden gemerkte Ansichten und Entwürfe getrennt gehalten."
        ]
      }
    ]
  },
  {
    id: "v0.1.0-beta.27",
    versionLabel: "Beta 27",
    publishedAt: "2026-08-20",
    publishedLabel: "20. August 2026",
    title: "Arbeitsbereiche und Preisprüfungen besser im Blick",
    intro:
      "Die wichtigsten Neuerungen für Buchhaltung, Einkauf, Controlling und Daten & Administration.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Erwartete Gutschriften haben jetzt eine eigene Übersicht mit eingelangten Gutschrift-Belegen und bestätigten Zuordnungen.",
          "Artikelansichten bieten mehrere Zeiträume und vergleichen Belegpreise mit belegten Best- oder Durchschnittspreisen.",
          "Die Übersicht führt direkt zu den Arbeitsbereichen Buchhaltung, Einkauf, Controlling und Daten & Administration.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Offene Preisprüfungen werden nach möglichen Mehrkosten priorisiert; für Ihren Betrieb lassen sich Vergleichszeitraum, Mindestabweichung und optionale Mindestmehrkosten festlegen.",
          "Die Artikelsuche bevorzugt exakte Artikel- und Alternativnummern und zeigt Einkäufe der letzten zwölf Monate sowie die Menge im laufenden Jahr.",
          "Artikel-, Beleg- und Hilfeseiten sind klarer gegliedert und besser lesbar; Navigation und Hinweise verwenden dieselben Arbeitsbereiche.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Herstellerähnliche Artikel werden nicht mehr als austauschbare Alternativen dargestellt.",
          "Arbeitsbereiche pausierter oder archivierter Mandanten bleiben gesperrt, bis der Mandant wieder aktiviert wird.",
          "Gleichzeitige Änderungen an Kostenstellen, Quellen und Importen werden zuverlässig nacheinander verarbeitet.",
        ],
      },
    ],
  },
  {
    id: "v0.1.0-beta.25",
    versionLabel: "Beta 25",
    publishedAt: "2026-07-26",
    publishedLabel: "26. Juli 2026",
    title: "Preisprüfungen schneller und sicherer bearbeiten",
    intro:
      "Die wichtigsten Verbesserungen für Rechnungen, Gutschriften, Einkauf und Preis-Analyse.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Auftragsbestätigungen können Sie jetzt vor der Freigabe gegen frühere Einkaufspreise prüfen.",
          "Erwartete Gutschriften lassen sich passenden Rechnungen zuordnen und bis zum vollständigen Ausgleich verfolgen.",
          "In der Preis-Analyse können Sie historische Preisspitzen finden, direkt zur betroffenen Position springen und ausgewählte Auffälligkeiten gemeinsam ausbuchen.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Bei der Belegprüfung sehen Sie den bisherigen Preisverlauf direkt an der Position und gelangen nach dem Speichern zur nächsten offenen Prüfung.",
          "Prüfzustände, Kommentare und Filter sind klarer getrennt; passende Hilfen erklären den Ablauf direkt auf der jeweiligen Seite.",
          "Mehrkosten und Preisabweichungen verwenden eine einheitliche Grundlage und berücksichtigen nur Preise, die am Belegdatum bereits bekannt waren.",
          "Der Qualitätsbericht unter Import & Quellen lädt auch bei umfangreichen Katalogdaten zuverlässig; auffällige Bereiche öffnen direkt eine verständliche Prüfliste.",
          "Der Prüfumfang lässt sich je Großhändler an die tatsächlich gelieferten Quellen anpassen; nicht anwendbare Prüfungen werden verständlich erklärt.",
          "Importe, Quellen-Inventar, Nachweisstatus und Prüfregeln sind klar getrennt; Prüflisten zeigen die entscheidenden Beleg-, Artikel- und Preiswerte und öffnen konkrete Nachweise direkt.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Rechnungs- und Gutschriftbeträge werden auch bei mehreren Stück und ungewöhnlichen Preisangaben korrekt dargestellt.",
          "Verschiedene Artikel, die beim Großhändler dieselbe Artikelnummer tragen, werden nicht mehr in einem gemeinsamen Preisverlauf vermischt.",
          "PDFs, Quellen und Prüfhinweise werden vollständiger und in Belegen sowie der Preis-Analyse einheitlicher dargestellt.",
        ],
      },
    ],
  },
  {
    id: "v0.1.0-beta.24",
    versionLabel: "Beta 24",
    publishedAt: "2026-07-05",
    publishedLabel: "5. Juli 2026",
    title: "Einkauf und Übersicht im Fokus",
    intro:
      "Die wichtigsten Änderungen für Buchhaltung, Einkauf und Geschäftsführung.",
    sections: [
      {
        kind: "new",
        title: "Neu",
        items: [
          "Die Übersicht zeigt jetzt direkt, womit Sie starten sollten: Suche, offene Preisprüfungen, Angebote und wichtige Kennzahlen.",
          "Preisanfragen und Angebote finden Sie jetzt gesammelt im Bereich Einkauf.",
          "Angebote können Sie direkt mit Ihren bisherigen Einkaufspreisen vergleichen.",
        ],
      },
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "Preisauffälligkeiten, Belege und Nachweise sind verständlicher beschrieben.",
          "Belege, PDFs, Quellen und Preisgrundlagen sind einheitlicher dargestellt.",
          "Die wichtigsten Einkaufsfunktionen sind schneller erreichbar.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "Tabellen bleiben auch in schmaleren Browserfenstern besser lesbar.",
          "Mengen, Preisauffälligkeiten und Status-Anzeigen verhalten sich stabiler.",
          "In kompakten Ansichten werden weniger Inhalte abgeschnitten.",
        ],
      },
    ],
  },
  {
    id: "v0.1.0-beta.23",
    versionLabel: "Beta 23",
    publishedAt: "2026-06-25",
    publishedLabel: "25. Juni 2026",
    title: "Belege und Preis-Analyse besser im Blick",
    intro: "Die wichtigsten Verbesserungen für Belege und Preis-Analyse.",
    sections: [
      {
        kind: "improved",
        title: "Verbessert",
        items: [
          "In der Preis-Analyse bleiben Datum und Beleg beim seitlichen Scrollen sichtbar.",
          "In der Belegliste sind Belegtyp und Details klarer voneinander getrennt.",
        ],
      },
      {
        kind: "fixed",
        title: "Behoben",
        items: [
          "PDF- und Auftragsnachweise werden in der Belegliste nicht mehr doppelt angezeigt.",
        ],
      },
    ],
  },
] as const satisfies readonly WhatsNewRelease[];

export const CURRENT_WHATS_NEW: WhatsNewRelease = WHATS_NEW_RELEASES[0];

export function getSeenWhatsNewReleaseId(): string | null {
  try {
    return window.localStorage.getItem(WHATS_NEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function markWhatsNewReleaseSeen(releaseId: string): void {
  try {
    window.localStorage.setItem(WHATS_NEW_STORAGE_KEY, releaseId);
  } catch {
    // localStorage kann im Privatmodus fehlen; die App bleibt dann nutzbar.
  }
}
